import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CatalogService, TrackingDashboardSummary } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-tracking-dashboard',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, TableModule, ButtonModule],
  template: `
    <ain-page [title]="t('catalog.trackingDashboard.title')" [description]="t('catalog.trackingDashboard.description')">
      <ain-inventory-page-hint pageKey="trackingDashboard" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!summary()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" />
      } @else {
        <div class="toolbar">
          <a [routerLink]="['/operations/control-room']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
        </div>

        <section class="stats">
          <article><strong>{{ t('catalog.controlRoom.totalLots') }}</strong><span>{{ summary()!.totalLots }}</span></article>
          <article><strong>{{ t('catalog.controlRoom.totalSerials') }}</strong><span>{{ summary()!.totalSerials }}</span></article>
          <article class="warn"><strong>{{ t('catalog.controlRoom.quarantined') }}</strong><span>{{ summary()!.quarantinedLots + summary()!.quarantinedSerials }}</span></article>
          <article class="warn"><strong>{{ t('catalog.controlRoom.expiring30') }}</strong><span>{{ summary()!.expiringWithin30Days }}</span></article>
        </section>

        <section class="panel">
          <h4>{{ t('catalog.trackingDashboard.expiringLots') }}</h4>
          <p-table [value]="summary()!.expiringLots">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.tracking.lotNumber') }}</th>
                <th>{{ t('catalog.tracking.expiryDate') }}</th>
                <th>{{ t('catalog.trackingDashboard.daysUntilExpiry') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [class.risk]="row.daysUntilExpiry <= 7">
                <td><a [routerLink]="['/inventory/lots', row.lotId]">{{ row.lotNumber }}</a></td>
                <td>{{ row.expiryDate || '-' }}</td>
                <td>{{ row.daysUntilExpiry }}</td>
                <td>{{ row.quantity }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 10px; align-items: center; margin-bottom: 12px; }
    .stats { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); margin-bottom: 12px; }
    .stats article { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 4px; }
    .stats article.warn { border-color: #fca5a5; background: #fef2f2; }
    .stats strong { font-size: 12px; color: #64748b; }
    .panel h4 { margin: 0 0 8px; }
    tr.risk { background: #fef2f2; }
    a { color: #1d4ed8; font-weight: 600; text-decoration: none; }
  `]
})
export class InventoryTrackingDashboardComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly summary = signal<TrackingDashboardSummary | null>(null);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.getTrackingDashboard().subscribe({
      next: summary => {
        this.summary.set(summary);
        this.loading.set(false);
      },
      error: () => {
        this.summary.set(null);
        this.loading.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
